import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Building2, Bell, Target, CheckCircle, ArrowRight, ShieldCheck } from "lucide-react";
import toast from "react-hot-toast";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

const INDUSTRIES = [
  "Oil & Gas", "Construction", "ICT & Technology", "Healthcare",
  "Education", "Agriculture", "Finance & Banking", "Engineering",
  "Logistics & Supply Chain", "Security Services", "Consulting",
  "Manufacturing", "Telecommunications", "Other",
];

const CATEGORIES = [
  "Works", "Goods", "Services", "Consultancy",
  "ICT", "Healthcare", "Education", "Oil & Gas", "Agriculture",
];

const NIGERIAN_STATES = [
  "Federal", "Lagos", "Rivers", "Abuja (FCT)", "Kano", "Ogun",
  "Delta", "Bayelsa", "Cross River", "Akwa Ibom", "Edo", "Imo",
  "Enugu", "Anambra", "Kaduna", "Oyo", "Osun", "Kwara", "Other",
];

const STEPS = [
  { num: 1, icon: Building2, label: "Company" },
  { num: 2, icon: Target,    label: "Focus"   },
  { num: 3, icon: Bell,      label: "Alerts"  },
];

export default function Onboarding() {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const [step, setStep]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm]       = useState({
    company_name:     "",
    rc_number:        "",
    industry:         "",
    state:            "",
    categories:       [],
    preferred_states: [],
    whatsapp_number:  "",
    email_alerts:     true,
    whatsapp_alerts:  false,
    agreed_to_terms:  false,
  });

  const update     = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleArr  = (key, val) => setForm(f => ({
    ...f,
    [key]: f[key].includes(val) ? f[key].filter(x => x !== val) : [...f[key], val],
  }));

  const handleComplete = async () => {
    if (!form.agreed_to_terms) {
      toast.error("Please accept the Terms of Use and Privacy Policy to continue");
      return;
    }
    setLoading(true);
    const token = localStorage.getItem("access_token");
    try {
      // 1. Update the company shell that was created at registration
      const compRes = await fetch(`${API}/auth/company`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name:                form.company_name || undefined,
          registration_number: form.rc_number    || undefined,
          industry:            form.industry     || undefined,
          state:               form.state        || undefined,
          preferred_categories: form.categories,
          preferred_states:    form.preferred_states,
        }),
      });
      if (!compRes.ok) {
        const d = await compRes.json();
        toast.error(d.detail || "Could not update company");
        setLoading(false);
        return;
      }

      // 2. Save alert preferences
      await fetch(`${API}/alerts/settings`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          whatsapp_number:  form.whatsapp_number,
          email_alerts:     form.email_alerts,
          whatsapp_alerts:  form.whatsapp_alerts && !!form.whatsapp_number,
        }),
      });

      localStorage.setItem("onboarding_done", "true");
      toast.success("All set! Welcome to ProTrackNG.");
      navigate("/dashboard");
      setTimeout(() => window.location.reload(), 300);
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const goStep1Next = () => {
    if (!form.company_name.trim()) { toast.error("Company name is required"); return; }
    if (!form.industry)            { toast.error("Please select your industry"); return; }
    setStep(2);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#080d1a", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px", fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(20px) } to { opacity:1; transform:translateY(0) } }
        .ob-card  { animation: fadeUp 0.4s ease both; }
        .ob-input { width:100%; padding:11px 14px; background:rgba(232,234,240,0.04); border:1px solid rgba(232,234,240,0.1); border-radius:10px; color:#e8eaf0; font-size:14px; font-family:inherit; outline:none; transition:border-color 0.2s; box-sizing:border-box; }
        .ob-input:focus { border-color:rgba(0,212,161,0.5); }
        .ob-input::placeholder { color:rgba(232,234,240,0.3); }
        .ob-chip  { display:inline-flex; align-items:center; padding:6px 12px; border-radius:8px; font-size:12px; font-weight:600; cursor:pointer; transition:all 0.15s; user-select:none; }
        .ob-btn   { width:100%; padding:13px; border-radius:10px; border:none; background:linear-gradient(135deg,#00d4a1,#00b888); color:#080d1a; font-size:14px; font-weight:700; cursor:pointer; font-family:inherit; transition:opacity 0.2s; display:flex; align-items:center; justify-content:center; gap:8px; }
        .ob-btn:hover    { opacity:0.88; }
        .ob-btn:disabled { opacity:0.5; cursor:not-allowed; }
        .ob-back  { flex:0 0 80px; padding:13px; border-radius:10px; border:1px solid rgba(232,234,240,0.1); background:transparent; color:rgba(232,234,240,0.5); font-size:13px; cursor:pointer; font-family:inherit; }
        @media (max-width:480px) { .ob-card { padding:28px 20px !important; } }
      `}</style>

      <div style={{ width: "100%", maxWidth: "520px" }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", marginBottom: "8px" }}>
            <div style={{ width: "36px", height: "36px", background: "linear-gradient(135deg,#00d4a1,#4f8ef7)", borderRadius: "9px", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "900", fontSize: "16px", color: "#080d1a" }}>P</div>
            <span style={{ fontSize: "20px", fontWeight: "800", color: "#fff" }}>ProTrack<span style={{ color: "#00d4a1" }}>NG</span></span>
          </div>
          <h1 style={{ fontSize: "24px", fontWeight: "800", color: "#fff", marginBottom: "6px" }}>
            Welcome, {user?.full_name?.split(" ")[0] || "there"}!
          </h1>
          <p style={{ fontSize: "14px", color: "rgba(232,234,240,0.5)" }}>Complete your profile to start tracking Nigerian tenders</p>
        </div>

        {/* Step indicators */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginBottom: "28px" }}>
          {STEPS.map((s, i) => (
            <React.Fragment key={s.num}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                <div style={{ width: "36px", height: "36px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: step > s.num ? "#00d4a1" : step === s.num ? "rgba(0,212,161,0.15)" : "rgba(232,234,240,0.05)", border: step >= s.num ? "2px solid #00d4a1" : "2px solid rgba(232,234,240,0.1)", transition: "all 0.25s" }}>
                  {step > s.num ? <CheckCircle size={16} color="#080d1a" /> : <s.icon size={15} color={step === s.num ? "#00d4a1" : "rgba(232,234,240,0.3)"} />}
                </div>
                <span style={{ fontSize: "10px", fontWeight: "600", color: step >= s.num ? "#00d4a1" : "rgba(232,234,240,0.3)", letterSpacing: "0.5px" }}>{s.label.toUpperCase()}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div style={{ width: "40px", height: "2px", background: step > s.num ? "#00d4a1" : "rgba(232,234,240,0.1)", transition: "background 0.3s", marginBottom: "14px" }} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Card */}
        <div className="ob-card" style={{ background: "rgba(20,29,46,0.95)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "20px", padding: "36px" }}>

          {/* STEP 1 — Company basics */}
          {step === 1 && (
            <div>
              <h2 style={{ fontSize: "18px", fontWeight: "800", color: "#fff", marginBottom: "4px" }}>Your Company</h2>
              <p style={{ fontSize: "13px", color: "rgba(232,234,240,0.5)", marginBottom: "24px" }}>Required to access tenders and AI matching. This prevents spammers and keeps quality high.</p>

              <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                <div>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "rgba(232,234,240,0.5)", display: "block", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Company Name *</label>
                  <input className="ob-input" value={form.company_name} onChange={e => update("company_name", e.target.value)} placeholder="Acme Nigeria Ltd" />
                </div>

                <div>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "rgba(232,234,240,0.5)", display: "block", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    CAC / RC Number
                    <span style={{ fontWeight: "400", marginLeft: "6px", color: "rgba(232,234,240,0.3)", textTransform: "none", fontSize: "11px" }}>recommended — prevents duplicates</span>
                  </label>
                  <input className="ob-input" value={form.rc_number} onChange={e => update("rc_number", e.target.value)} placeholder="RC-123456 or BN-654321" />
                </div>

                <div>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "rgba(232,234,240,0.5)", display: "block", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Industry *</label>
                  <select className="ob-input" value={form.industry} onChange={e => update("industry", e.target.value)} style={{ appearance: "none", cursor: "pointer" }}>
                    <option value="">Select your industry</option>
                    {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "rgba(232,234,240,0.5)", display: "block", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Primary State of Operation</label>
                  <select className="ob-input" value={form.state} onChange={e => update("state", e.target.value)} style={{ appearance: "none", cursor: "pointer" }}>
                    <option value="">Select state</option>
                    {NIGERIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                {/* Anti-spam notice */}
                <div style={{ display: "flex", gap: "10px", alignItems: "flex-start", padding: "12px 14px", background: "rgba(79,142,247,0.06)", border: "1px solid rgba(79,142,247,0.15)", borderRadius: "10px" }}>
                  <ShieldCheck size={15} color="#4f8ef7" style={{ flexShrink: 0, marginTop: "1px" }} />
                  <p style={{ fontSize: "12px", color: "rgba(232,234,240,0.5)", lineHeight: "1.5", margin: 0 }}>
                    ProTrackNG is for registered Nigerian businesses. Profile details help us verify legitimacy and personalise your tender feed.
                  </p>
                </div>
              </div>

              <button className="ob-btn" style={{ marginTop: "24px" }} onClick={goStep1Next}>
                Continue <ArrowRight size={15} />
              </button>
            </div>
          )}

          {/* STEP 2 — Tender focus */}
          {step === 2 && (
            <div>
              <h2 style={{ fontSize: "18px", fontWeight: "800", color: "#fff", marginBottom: "4px" }}>Tender Focus</h2>
              <p style={{ fontSize: "13px", color: "rgba(232,234,240,0.5)", marginBottom: "24px" }}>Select what you want to track — powers your AI matching engine.</p>

              <div>
                <label style={{ fontSize: "12px", fontWeight: "600", color: "rgba(232,234,240,0.5)", display: "block", marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Tender Categories</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "20px" }}>
                  {CATEGORIES.map(cat => {
                    const sel = form.categories.includes(cat);
                    return (
                      <button key={cat} className="ob-chip" onClick={() => toggleArr("categories", cat)}
                        style={{ background: sel ? "rgba(0,212,161,0.12)" : "rgba(232,234,240,0.04)", border: `1px solid ${sel ? "rgba(0,212,161,0.4)" : "rgba(232,234,240,0.1)"}`, color: sel ? "#00d4a1" : "rgba(232,234,240,0.6)" }}>
                        {sel ? "✓ " : ""}{cat}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label style={{ fontSize: "12px", fontWeight: "600", color: "rgba(232,234,240,0.5)", display: "block", marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.5px" }}>States to Watch</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {NIGERIAN_STATES.slice(0, 14).map(st => {
                    const sel = form.preferred_states.includes(st);
                    return (
                      <button key={st} className="ob-chip" onClick={() => toggleArr("preferred_states", st)}
                        style={{ background: sel ? "rgba(79,142,247,0.12)" : "rgba(232,234,240,0.04)", border: `1px solid ${sel ? "rgba(79,142,247,0.4)" : "rgba(232,234,240,0.1)"}`, color: sel ? "#4f8ef7" : "rgba(232,234,240,0.6)" }}>
                        {sel ? "✓ " : ""}{st}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: "flex", gap: "10px", marginTop: "28px" }}>
                <button className="ob-back" onClick={() => setStep(1)}>Back</button>
                <button className="ob-btn" style={{ flex: 1 }} onClick={() => setStep(3)}>Continue <ArrowRight size={15} /></button>
              </div>
            </div>
          )}

          {/* STEP 3 — Alerts */}
          {step === 3 && (
            <div>
              <h2 style={{ fontSize: "18px", fontWeight: "800", color: "#fff", marginBottom: "4px" }}>Alert Preferences</h2>
              <p style={{ fontSize: "13px", color: "rgba(232,234,240,0.5)", marginBottom: "24px" }}>Never miss a deadline. You can change these any time from Settings.</p>

              {/* Email toggle */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", background: "rgba(232,234,240,0.03)", border: "1px solid rgba(232,234,240,0.08)", borderRadius: "12px", marginBottom: "12px" }}>
                <div>
                  <div style={{ fontSize: "14px", fontWeight: "600", color: "#fff", marginBottom: "2px" }}>Email Alerts</div>
                  <div style={{ fontSize: "12px", color: "rgba(232,234,240,0.4)" }}>Deadline reminders to {user?.email}</div>
                </div>
                <button onClick={() => update("email_alerts", !form.email_alerts)}
                  style={{ width: "44px", height: "24px", borderRadius: "12px", border: "none", cursor: "pointer", background: form.email_alerts ? "#00d4a1" : "rgba(232,234,240,0.15)", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
                  <div style={{ width: "18px", height: "18px", borderRadius: "50%", background: "#fff", position: "absolute", top: "3px", left: form.email_alerts ? "23px" : "3px", transition: "left 0.2s" }} />
                </button>
              </div>

              {/* WhatsApp toggle */}
              <div style={{ padding: "14px 16px", background: "rgba(232,234,240,0.03)", border: "1px solid rgba(232,234,240,0.08)", borderRadius: "12px", marginBottom: "24px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: form.whatsapp_alerts ? "14px" : 0 }}>
                  <div>
                    <div style={{ fontSize: "14px", fontWeight: "600", color: "#fff", marginBottom: "2px" }}>WhatsApp Alerts</div>
                    <div style={{ fontSize: "12px", color: "rgba(232,234,240,0.4)" }}>Instant deadline alerts via WhatsApp</div>
                  </div>
                  <button onClick={() => update("whatsapp_alerts", !form.whatsapp_alerts)}
                    style={{ width: "44px", height: "24px", borderRadius: "12px", border: "none", cursor: "pointer", background: form.whatsapp_alerts ? "#00d4a1" : "rgba(232,234,240,0.15)", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
                    <div style={{ width: "18px", height: "18px", borderRadius: "50%", background: "#fff", position: "absolute", top: "3px", left: form.whatsapp_alerts ? "23px" : "3px", transition: "left 0.2s" }} />
                  </button>
                </div>
                {form.whatsapp_alerts && (
                  <div>
                    <input className="ob-input" value={form.whatsapp_number} onChange={e => update("whatsapp_number", e.target.value)} placeholder="+234 801 234 5678" />
                    <p style={{ fontSize: "11px", color: "rgba(232,234,240,0.3)", marginTop: "6px" }}>Include country code. First send "join [word]" to +14155238886 on WhatsApp to activate.</p>
                  </div>
                )}
              </div>

              {/* Terms & Privacy acceptance */}
              <label style={{ display: "flex", gap: "10px", alignItems: "flex-start", padding: "14px 16px", background: "rgba(232,234,240,0.03)", border: `1px solid ${form.agreed_to_terms ? "rgba(0,212,161,0.3)" : "rgba(232,234,240,0.08)"}`, borderRadius: "12px", marginBottom: "20px", cursor: "pointer", transition: "border-color 0.2s" }}>
                <input
                  type="checkbox"
                  checked={form.agreed_to_terms}
                  onChange={e => update("agreed_to_terms", e.target.checked)}
                  style={{ width: "16px", height: "16px", marginTop: "1px", flexShrink: 0, accentColor: "#00d4a1", cursor: "pointer" }}
                />
                <span style={{ fontSize: "12px", color: "rgba(232,234,240,0.6)", lineHeight: "1.6" }}>
                  I agree to ProTrackNG's{" "}
                  <a href="#/terms" target="_blank" rel="noopener noreferrer" style={{ color: "#00d4a1", textDecoration: "none", fontWeight: "600" }} onClick={e => e.stopPropagation()}>
                    Terms of Use
                  </a>{" "}
                  and{" "}
                  <a href="#/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#00d4a1", textDecoration: "none", fontWeight: "600" }} onClick={e => e.stopPropagation()}>
                    Privacy Policy
                  </a>.
                </span>
              </label>

              <div style={{ display: "flex", gap: "10px" }}>
                <button className="ob-back" onClick={() => setStep(2)}>Back</button>
                <button className="ob-btn" style={{ flex: 1 }} disabled={loading || !form.agreed_to_terms} onClick={handleComplete}>
                  {loading ? "Setting up..." : <><CheckCircle size={15} /> Start Tracking Tenders</>}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* No skip button — onboarding is required */}
        <p style={{ textAlign: "center", marginTop: "16px", fontSize: "11px", color: "rgba(232,234,240,0.2)" }}>
          ProTrackNG is for registered Nigerian businesses only
        </p>
      </div>
    </div>
  );
}
